import i18n from '../../../../../assets/utils/comm/i18n';
import { formatDateWithWeekday } from '../../../../../assets/utils/comm/dateUtils';

function parseTime(date) {
  if (date == null) return NaN;
  if (typeof date === 'object' && date instanceof Date) return date.getTime();
  if (typeof date === 'number') return date;
  return new Date(date).getTime();
}

function hasWeekdayToken(text) {
  return /\(([일월화수목금토]|Sun|Mon|Tue|Wed|Thu|Fri|Sat)\)/.test(String(text || ''));
}

function formatAbsoluteWithWeekday(t) {
  const d = new Date(t);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const sec = String(d.getSeconds()).padStart(2, '0');
  const datePart = `${y}-${m}-${day}`;
  const withWeekday = formatDateWithWeekday(datePart) || datePart;
  return `${withWeekday} ${h}:${min}:${sec}`;
}

function tRelative(key, count) {
  try {
    if (i18n?.isInitialized) {
      return count != null ? i18n.t(key, { count }) : i18n.t(key);
    }
  } catch {
    /* ignore */
  }
  const koFallback = {
    'common.date.relativeJustNow': '방금 전',
    'common.date.relativeMinutesAgo': `${count}분 전`,
    'common.date.relativeHoursAgo': `${count}시간 전`,
    'common.date.relativeDaysAgo': `${count}일 전`,
    'common.date.relativeWeeksAgo': `${count}주 전`,
    'common.date.relativeMonthsAgo': `${count}개월 전`,
    'common.date.relativeYearsAgo': `${count}년 전`,
  };
  const enFallback = {
    'common.date.relativeJustNow': 'Just now',
    'common.date.relativeMinutesAgo': `${count} min ago`,
    'common.date.relativeHoursAgo': `${count} hr ago`,
    'common.date.relativeDaysAgo': `${count} days ago`,
    'common.date.relativeWeeksAgo': `${count} wk ago`,
    'common.date.relativeMonthsAgo': `${count} mo ago`,
    'common.date.relativeYearsAgo': `${count} yr ago`,
  };
  const lang = String(i18n?.language || 'ko').split('-')[0].toLowerCase();
  const table = lang === 'en' ? enFallback : koFallback;
  return table[key] ?? String(count ?? '');
}

function getRelativePart(t, nowMs) {
  const diffMs = nowMs - t;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  if (diffSec < 0) return tRelative('common.date.relativeJustNow');
  if (diffSec < 60) return tRelative('common.date.relativeJustNow');
  if (diffMin < 60) return tRelative('common.date.relativeMinutesAgo', diffMin);
  if (diffHour < 24) return tRelative('common.date.relativeHoursAgo', diffHour);
  if (diffDay < 7) return tRelative('common.date.relativeDaysAgo', diffDay);
  if (diffWeek < 4) return tRelative('common.date.relativeWeeksAgo', diffWeek);
  const past = new Date(t);
  const nowDate = new Date(nowMs);
  const diffMonth = (nowDate.getFullYear() - past.getFullYear()) * 12 + (nowDate.getMonth() - past.getMonth());
  if (diffMonth < 1) return tRelative('common.date.relativeWeeksAgo', diffWeek);
  if (diffMonth < 12) return tRelative('common.date.relativeMonthsAgo', diffMonth);
  const diffYear = nowDate.getFullYear() - past.getFullYear();
  return tRelative('common.date.relativeYearsAgo', diffYear);
}

/**
 * 날짜/시간을 상대 시간 문자열로 변환 (화면 표시용)
 */
export function formatRelativeTime(date, now = new Date()) {
  const t = parseTime(date);
  if (Number.isNaN(t)) return String(date ?? '');
  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  return getRelativePart(t, nowMs);
}

/**
 * yyyy-mm-dd hh:mm:ss (n일 전) 형식으로 표시
 */
export function formatDateTimeWithRelative(date, now = new Date()) {
  const t = parseTime(date);
  if (Number.isNaN(t)) return String(date ?? '');
  const raw = String(date ?? '').trim();
  const absolute = hasWeekdayToken(raw)
    ? raw
    : formatAbsoluteWithWeekday(t);
  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  const relative = getRelativePart(t, nowMs);
  return `${absolute} (${relative})`;
}
